import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent,
  type ReactNode,
} from "react";
import { Link, useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { z } from "zod";
import { ArrowLeft, ArrowRight, Check, CornerDownLeft, ShieldCheck } from "lucide-react";
import { api, ApiError, clearCsrfToken } from "@/lib/api";
import { sessionQueryKey } from "@/lib/session";
import { Button } from "@/components/ui/Button";
import { Alert } from "@/components/ui/Feedback";
import { cn } from "@/lib/cn";

const registerSchema = z.looseObject({
  rm_number: z.string(),
  redirect: z.string().default("/patient/dashboard"),
});

/** Mirrors validate_password() in app.py, shown live rather than after submit. */
const PASSWORD_RULES: { label: string; test: (value: string) => boolean }[] = [
  { label: "Minimal 8 karakter", test: (v) => v.length >= 8 },
  { label: "Ada huruf besar", test: (v) => /[A-Z]/.test(v) },
  { label: "Ada huruf kecil", test: (v) => /[a-z]/.test(v) },
  { label: "Ada angka", test: (v) => /[0-9]/.test(v) },
];

type Values = Record<string, string>;

type Step = {
  key: string;
  /** The question, phrased as a person would ask it. */
  question: string;
  hint?: string;
  optional?: boolean;
  type?: "text" | "email" | "tel" | "date" | "password" | "choice";
  inputMode?: "text" | "numeric" | "tel" | "email";
  autoComplete?: string;
  placeholder?: string;
  maxLength?: number;
  choices?: string[];
  /** Second field on the same step — only used for password confirmation. */
  confirmKey?: string;
  confirmQuestion?: string;
  /** Returns an error message, or null when the answer is acceptable. */
  validate?: (value: string, all: Values) => string | null;
  transform?: (value: string) => string;
};

const STEPS: Step[] = [
  {
    key: "name",
    question: "Hai, silakan isi nama lengkapmu!",
    hint: "Sesuai KTP, agar cocok dengan rekam medis.",
    autoComplete: "name",
    validate: (v) => (v.trim().length < 3 ? "Nama minimal 3 karakter." : null),
  },
  {
    key: "nik",
    question: "Berapa nomor NIK kamu?",
    hint: "16 digit angka pada KTP.",
    inputMode: "numeric",
    maxLength: 16,
    placeholder: "3201010101010001",
    transform: (v) => v.replace(/\D/g, "").slice(0, 16),
    validate: (v) => (/^\d{16}$/.test(v) ? null : "NIK harus tepat 16 digit angka."),
  },
  {
    key: "dob",
    question: "Kapan tanggal lahirmu?",
    // A masked text field, not <input type="date">: the native picker opens on
    // the current month, so entering a birth year means scrolling back decades.
    // Typing eight digits is faster on desktop and mobile alike.
    hint: "Tulis tanggal lahir, contoh 15/06/1994.",
    inputMode: "numeric",
    autoComplete: "bday",
    placeholder: "DD/MM/YYYY",
    maxLength: 10,
    transform: (v) => {
      const digits = v.replace(/\D/g, "").slice(0, 8);
      const parts = [digits.slice(0, 2), digits.slice(2, 4), digits.slice(4, 8)];
      return parts.filter(Boolean).join("/");
    },
    validate: (v) => {
      const match = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(v);
      if (!match) return "Tulis dalam format DD/MM/YYYY.";
      const [, day, month, year] = match;
      const date = new Date(`${year}-${month}-${day}T00:00:00`);
      // Round-trips through Date to reject 31/02 and similar impossible dates.
      if (
        Number.isNaN(date.getTime()) ||
        date.getDate() !== Number(day) ||
        date.getMonth() + 1 !== Number(month)
      ) {
        return "Tanggal tersebut tidak ada.";
      }
      if (date > new Date()) return "Tanggal lahir tidak boleh di masa depan.";
      if (Number(year) < 1900) return "Periksa kembali tahun lahirnya.";
      return null;
    },
  },
  {
    key: "gender",
    question: "Jenis kelamin kamu?",
    type: "choice",
    choices: ["Laki-laki", "Perempuan"],
    validate: (v) => (v ? null : "Pilih salah satu."),
  },
  {
    key: "phone",
    question: "Nomor telepon yang bisa dihubungi?",
    hint: "Dipakai bila tim medis perlu menghubungimu.",
    type: "tel",
    inputMode: "tel",
    autoComplete: "tel",
    placeholder: "0812xxxxxxxx",
    validate: (v) => (v.replace(/\D/g, "").length < 9 ? "Nomor telepon tidak valid." : null),
  },
  {
    key: "address",
    question: "Di mana alamat tinggalmu?",
    autoComplete: "street-address",
    validate: (v) => (v.trim().length < 5 ? "Alamat terlalu singkat." : null),
  },
  {
    key: "email",
    question: "Alamat email kamu?",
    hint: "Dipakai untuk memulihkan password bila lupa.",
    type: "email",
    inputMode: "email",
    autoComplete: "email",
    placeholder: "nama@email.com",
    transform: (v) => v.trim(),
    validate: (v) =>
      /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(v) ? null : "Format email belum benar.",
  },
  {
    key: "username",
    question: "Mau pakai username apa?",
    hint: "Dipakai setiap kali masuk ke portal.",
    autoComplete: "username",
    transform: (v) => v.trim().replace(/\s+/g, ""),
    validate: (v) => (v.length < 4 ? "Username minimal 4 karakter." : null),
  },
  {
    key: "no_bpjs",
    question: "Punya nomor BPJS?",
    hint: "Boleh dilewati kalau tidak ada.",
    optional: true,
    inputMode: "numeric",
    transform: (v) => v.replace(/\D/g, ""),
  },
  {
    key: "password",
    question: "Terakhir, buat password kamu.",
    type: "password",
    autoComplete: "new-password",
    confirmKey: "password_confirmation",
    confirmQuestion: "Ulangi password",
    validate: (v, all) => {
      const failed = PASSWORD_RULES.find((rule) => !rule.test(v));
      if (failed) return failed.label + " belum terpenuhi.";
      if (all.password_confirmation && v !== all.password_confirmation) {
        return "Konfirmasi password tidak sama.";
      }
      if (!all.password_confirmation) return "Ulangi password untuk konfirmasi.";
      return null;
    },
  },
];

/**
 * Conversational registration.
 *
 * One question per screen rather than a wall of fields: patients fill this in
 * on a phone, often in a waiting room, and a 10-field grid is where they give
 * up. Each answer is validated as it is given, so a mistake surfaces next to
 * the question that caused it instead of as a list at the end.
 */
export function RegisterScreen() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [index, setIndex] = useState(0);
  const [values, setValues] = useState<Values>({});
  const [error, setError] = useState<string | null>(null);
  const [serverErrors, setServerErrors] = useState<string[]>([]);
  const [pending, setPending] = useState(false);
  const [leaving, setLeaving] = useState<"forward" | "back" | null>(null);

  const inputRef = useRef<HTMLInputElement>(null);
  const step = STEPS[index];
  const isReview = index === STEPS.length;
  const total = STEPS.length + 1;

  // Focus follows the question, so the keyboard is ready without a tap.
  useEffect(() => {
    if (isReview) return;
    const timer = setTimeout(() => inputRef.current?.focus(), 120);
    return () => clearTimeout(timer);
  }, [index, isReview]);

  const set = useCallback((key: string, value: string) => {
    setValues((current) => ({ ...current, [key]: value }));
    setError(null);
  }, []);

  const goTo = useCallback((next: number, direction: "forward" | "back") => {
    setLeaving(direction);
    setError(null);
    // Short enough that it reads as responsive, long enough to show direction.
    setTimeout(() => {
      setIndex(next);
      setLeaving(null);
    }, 140);
  }, []);

  function advance() {
    if (isReview) return;
    const value = values[step.key] ?? "";
    if (!step.optional || value) {
      const message = step.validate?.(value, values);
      if (message) {
        setError(message);
        inputRef.current?.focus();
        return;
      }
    }
    goTo(index + 1, "forward");
  }

  function back() {
    if (index === 0) return;
    goTo(index - 1, "back");
  }

  function handleKeyDown(event: KeyboardEvent<HTMLInputElement>) {
    if (event.key === "Enter") {
      event.preventDefault();
      advance();
    }
  }

  async function submit() {
    setServerErrors([]);
    setPending(true);
    try {
      // The column stores whatever it is given, and every other screen reads
      // dob as ISO — so the display format is converted here, not persisted.
      const [day, month, year] = (values.dob ?? "").split("/");
      const payload = {
        ...values,
        dob: year && month && day ? `${year}-${month}-${day}` : "",
      };
      const result = await api.post("/api/auth/patient/register", registerSchema, payload);
      clearCsrfToken();
      await queryClient.invalidateQueries({ queryKey: sessionQueryKey });
      navigate(result.redirect, { replace: true });
    } catch (cause) {
      if (cause instanceof ApiError) {
        const payload = cause.payload as { errors?: string[] } | null;
        setServerErrors(payload?.errors?.length ? payload.errors : [cause.message]);
      } else {
        setServerErrors(["Tidak dapat terhubung ke server."]);
      }
    } finally {
      setPending(false);
    }
  }

  const progress = Math.round((index / (total - 1)) * 100);
  const answered = useMemo(
    () => STEPS.filter((item) => !item.optional).every((item) => values[item.key]),
    [values],
  );

  return (
    // Flex column rather than a hand-computed content height: the footer hint
    // was being pushed off screen whenever the header or hint wrapped.
    <main className="bg-canvas relative flex min-h-dvh flex-col overflow-hidden">
      {/* Soft glow behind the question — the calm, unhurried feel of the
          reference, rendered in the product's own palette. */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            "radial-gradient(60% 50% at 50% 40%, var(--color-flux-400), transparent 70%)",
          opacity: 0.07,
        }}
      />

      <div className="border-hairline/60 fixed inset-x-0 top-0 z-20 h-0.5 border-b">
        <div
          className="bg-accent h-full transition-[width] duration-300 ease-(--ease-out-soft)"
          style={{ width: `${progress}%` }}
        />
      </div>

      <header className="relative z-10 flex items-center justify-between px-5 py-5 sm:px-8">
        <Link to="/" className="flex items-center gap-2.5">
          <span
            aria-hidden
            className="bg-accent text-accent-content font-display grid size-8 place-items-center text-base font-extrabold"
            style={{ borderRadius: "0.7rem 0.2rem 0.7rem 0.2rem" }}
          >
            M
          </span>
          <span className="font-display leading-none font-extrabold tracking-tight">
            mediora
          </span>
        </Link>
        <p className="text-content-subtle tabular text-xs">
          {isReview ? "Tinjau" : `${index + 1} / ${STEPS.length}`}
        </p>
      </header>

      {/* Announced on every step change so a screen reader user knows where
          they are without hunting for the heading. */}
      <p aria-live="polite" className="sr-only">
        {isReview
          ? "Langkah terakhir: tinjau data Anda"
          : `Pertanyaan ${index + 1} dari ${STEPS.length}: ${step.question}`}
      </p>

      <div className="relative z-10 mx-auto flex w-full max-w-2xl flex-1 items-center px-5 py-8 sm:px-8">
        <div
          key={isReview ? "review" : step.key}
          className={cn(
            "w-full transition-all duration-150 ease-(--ease-out-soft)",
            leaving === "forward" && "-translate-y-3 opacity-0",
            leaving === "back" && "translate-y-3 opacity-0",
            !leaving && "translate-y-0 opacity-100",
          )}
        >
          {isReview ? (
            <ReviewStep
              values={values}
              errors={serverErrors}
              onEdit={(key) => {
                const target = STEPS.findIndex((item) => item.key === key);
                if (target >= 0) goTo(target, "back");
              }}
            />
          ) : (
            <QuestionStep
              step={step}
              values={values}
              error={error}
              inputRef={inputRef}
              onChange={set}
              onKeyDown={handleKeyDown}
              onChoose={(value) => {
                set(step.key, value);
                // A single-tap answer should not also need the arrow.
                setTimeout(() => goTo(index + 1, "forward"), 160);
              }}
            />
          )}
        </div>
      </div>

      <footer className="relative z-10 flex shrink-0 flex-col items-center gap-3 px-5 pb-8">
        <div className="flex items-center gap-2.5">
          <button
            type="button"
            onClick={back}
            disabled={index === 0 || Boolean(leaving)}
            aria-label="Pertanyaan sebelumnya"
            className={cn(
              "bg-surface-sunken text-content-muted hover:text-content grid size-11 place-items-center rounded-full",
              "transition-colors disabled:pointer-events-none disabled:opacity-30",
            )}
          >
            <ArrowLeft className="size-4" />
          </button>

          {isReview ? (
            <Button
              variant="primary"
              size="lg"
              loading={pending}
              disabled={!answered}
              onClick={submit}
              icon={<Check className="size-4" />}
            >
              Buat akun
            </Button>
          ) : (
            <button
              type="button"
              onClick={advance}
              disabled={Boolean(leaving)}
              aria-label="Pertanyaan berikutnya"
              className={cn(
                "bg-accent text-accent-content shadow-accent grid h-11 w-16 place-items-center rounded-full",
                "transition-transform active:translate-y-px disabled:opacity-60",
              )}
            >
              <ArrowRight className="size-4" />
            </button>
          )}
        </div>

        {!isReview && (
          <p className="text-content-subtle hidden items-center gap-1.5 text-xs sm:flex">
            Tekan
            <kbd className="border-hairline-strong bg-surface inline-flex items-center gap-1 rounded border px-1.5 py-0.5 font-sans">
              Enter <CornerDownLeft className="size-3" />
            </kbd>
            untuk lanjut
          </p>
        )}
      </footer>
    </main>
  );
}

function QuestionStep({
  step,
  values,
  error,
  inputRef,
  onChange,
  onKeyDown,
  onChoose,
}: {
  step: Step;
  values: Values;
  error: string | null;
  inputRef: React.RefObject<HTMLInputElement | null>;
  onChange: (key: string, value: string) => void;
  onKeyDown: (event: KeyboardEvent<HTMLInputElement>) => void;
  onChoose: (value: string) => void;
}) {
  const value = values[step.key] ?? "";
  const fieldId = `step-${step.key}`;

  const underline =
    "w-full border-0 border-b bg-transparent pb-2.5 text-xl sm:text-2xl outline-none " +
    "placeholder:text-content-subtle/50 transition-colors";

  return (
    <>
      <label htmlFor={fieldId} className="font-display block text-2xl sm:text-3xl">
        {step.question}
        {!step.optional && (
          <span aria-hidden className="text-accent ml-1">
            *
          </span>
        )}
      </label>
      {step.hint && <p className="text-content-muted mt-2 text-sm">{step.hint}</p>}

      <div className="mt-8 space-y-4">
        {step.type === "choice" ? (
          <div className="flex flex-wrap gap-2.5">
            {step.choices?.map((choice) => (
              <button
                key={choice}
                type="button"
                onClick={() => onChoose(choice)}
                aria-pressed={value === choice}
                className={cn(
                  "rounded-(--radius-control) border px-6 py-3 text-base font-semibold transition-colors",
                  value === choice
                    ? "border-accent bg-accent text-accent-content"
                    : "border-hairline-strong hover:border-accent/50",
                )}
              >
                {choice}
              </button>
            ))}
          </div>
        ) : (
          <input
            ref={inputRef}
            id={fieldId}
            type={step.type ?? "text"}
            value={value}
            inputMode={step.inputMode}
            autoComplete={step.autoComplete}
            placeholder={step.placeholder}
            maxLength={step.maxLength}
            aria-invalid={error ? true : undefined}
            aria-describedby={error ? `${fieldId}-error` : undefined}
            onKeyDown={onKeyDown}
            onChange={(event) =>
              onChange(step.key, step.transform ? step.transform(event.target.value) : event.target.value)
            }
            className={cn(underline, error ? "border-critical-500" : "border-hairline-strong focus:border-accent")}
          />
        )}

        {step.confirmKey && (
          <input
            type="password"
            value={values[step.confirmKey] ?? ""}
            autoComplete="new-password"
            placeholder={step.confirmQuestion}
            aria-label={step.confirmQuestion}
            onKeyDown={onKeyDown}
            onChange={(event) => onChange(step.confirmKey!, event.target.value)}
            className={cn(underline, "border-hairline-strong focus:border-accent text-lg sm:text-xl")}
          />
        )}

        {step.type === "password" && (
          <ul className="flex flex-wrap gap-x-4 gap-y-1.5 pt-1">
            {PASSWORD_RULES.map((rule) => {
              const met = rule.test(value);
              return (
                <li
                  key={rule.label}
                  className={cn(
                    "inline-flex items-center gap-1.5 text-xs",
                    met ? "text-routine-text" : "text-content-subtle",
                  )}
                >
                  <Check aria-hidden className={cn("size-3.5", !met && "opacity-30")} />
                  {rule.label}
                </li>
              );
            })}
          </ul>
        )}

        {error && (
          <p id={`${fieldId}-error`} role="alert" className="text-critical-text text-sm font-medium">
            {error}
          </p>
        )}
      </div>
    </>
  );
}

const REVIEW_LABELS: Record<string, string> = {
  name: "Nama lengkap",
  nik: "NIK",
  dob: "Tanggal lahir",
  gender: "Jenis kelamin",
  phone: "Telepon",
  address: "Alamat",
  email: "Email",
  username: "Username",
  no_bpjs: "Nomor BPJS",
};

function ReviewStep({
  values,
  errors,
  onEdit,
}: {
  values: Values;
  errors: string[];
  onEdit: (key: string) => void;
}) {
  return (
    <>
      <h1 className="font-display text-2xl sm:text-3xl">Sudah benar semua?</h1>
      <p className="text-content-muted mt-2 text-sm">
        Periksa sekali lagi. Ketuk salah satu baris untuk mengubahnya.
      </p>

      {errors.length > 0 && (
        <Alert tone="danger" title="Pendaftaran belum bisa diproses" className="mt-5">
          <ul className={cn(errors.length > 1 && "list-disc space-y-0.5 pl-4")}>
            {errors.map((message) => (
              <li key={message} className={cn(errors.length === 1 && "list-none")}>
                {message}
              </li>
            ))}
          </ul>
        </Alert>
      )}

      <dl className="divide-hairline border-hairline mt-6 divide-y border-y">
        {Object.entries(REVIEW_LABELS).map(([key, label]) => (
          <button
            key={key}
            type="button"
            onClick={() => onEdit(key)}
            className="hover:bg-surface-sunken flex w-full items-center justify-between gap-4 px-1 py-3 text-left transition-colors"
          >
            <dt className="text-content-subtle shrink-0 text-sm">{label}</dt>
            <dd className="min-w-0 truncate text-sm font-medium">
              {values[key] || <span className="text-content-subtle">—</span>}
            </dd>
          </button>
        ))}
      </dl>

      <p className="text-content-subtle mt-5 flex items-start gap-2 text-xs">
        <ShieldCheck aria-hidden className="mt-px size-3.5 shrink-0" />
        <span>
          Data kamu dipakai untuk layanan medis RS Mediora dan tidak dibagikan ke pihak ketiga
          tanpa persetujuan. Nomor rekam medis dibuat otomatis setelah akun jadi.
        </span>
      </p>

      <p className="text-content-muted mt-4 text-sm">
        Sudah punya akun?{" "}
        <Link to="/patient/login" className="text-accent font-semibold hover:underline">
          Masuk di sini
        </Link>
      </p>
    </>
  );
}

/** Kept for the password-recovery screen, which shares the brand lockup. */
export function Brand({ children }: { children?: ReactNode }) {
  return (
    <div className="mb-7 flex items-center gap-2.5">
      <span
        aria-hidden
        className="bg-accent text-accent-content font-display grid size-9 place-items-center text-lg font-extrabold"
        style={{ borderRadius: "0.75rem 0.25rem 0.75rem 0.25rem" }}
      >
        M
      </span>
      <span className="font-display text-lg leading-none font-extrabold tracking-tight">
        mediora
      </span>
      {children}
    </div>
  );
}
